/**
 * Users API Mappers
 * Backend returns nested structure:
 * {
 *   identityId: number,
 *   tenantId: number,
 *   isAdmin: boolean,
 *   Identity: { id, username, tenantid, ... }
 * }
 */
import type { TenantUser } from '@/types';
import { logger } from '@/utils/logger';

interface ApiTenantUser {
  identityId: number;
  tenantId: number;
  isAdmin: boolean;
  Identity: {
    id: number;
    username: string;
    tenantid: number;
  };
}

/**
 * Convert API user to frontend user
 * API returns nested structure: { identityId, tenantId, isAdmin, Identity: { username, ... } }
 */
export const fromApiUser = (apiUser: ApiTenantUser): TenantUser => {
  // Extract from nested Identity object (handle missing Identity)
  const identity = apiUser.Identity || {};

  return {
    id: apiUser.identityId ?? identity.id,
    username: identity.username ?? 'Unknown User',
    admin: apiUser.isAdmin ?? false,
    tenantid: String(apiUser.tenantId ?? identity.tenantid),
  };
};

/**
 * Convert array of API users to frontend users
 * Filters out users without valid Identity (matches dataProtect userPicker.js line 74)
 */
export const fromApiUsers = (apiUsers: ApiTenantUser[]): TenantUser[] => {
  if (!Array.isArray(apiUsers)) {
    logger.error('fromApiUsers: expected array, got:', typeof apiUsers);
    return [];
  }

  return apiUsers
    .filter((apiUser) => {
      // Match legacy dataProtect behavior: only include users with valid Identity
      // (dataProtect userPicker.js line 74: p.Identity && p.Identity.id > 0)
      const identity = apiUser.Identity;

      if (!identity) return false;
      if (!identity.id || identity.id <= 0) return false;
      if (!identity.username || identity.username.trim() === '') return false;

      return true;
    })
    .map(fromApiUser);
};
